import React from "react";
import { FaBed, FaLock, FaCheckCircle, FaCreditCard } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";

const pricingTiers = [
  {
    name: "Small",
    bedRange: "4–50 beds",
    baseMonthly: "$250",
    maintenance: "$75",
    support: "$25",
    setup: "$500",
    icon: <FaBed className="text-blue-500 text-3xl mb-2" />,
    popular: false,
    key: "small",
  },
  {
    name: "Medium",
    bedRange: "51–80 beds",
    baseMonthly: "$500",
    maintenance: "$100",
    support: "$50",
    setup: "$1000",
    icon: (
      <div className="flex gap-1 mb-2 text-blue-600">
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
      </div>
    ),
    popular: true,
    key: "medium",
  },
  {
    name: "Large-Medium",
    bedRange: "81–125 beds",
    baseMonthly: "-",
    maintenance: "$125",
    support: "$75",
    setup: "$1500",
    icon: (
      <div className="flex gap-1 mb-2 text-blue-400">
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
      </div>
    ),
    popular: false,
    key: "large-medium",
  },
  {
    name: "Large",
    bedRange: "126+ beds",
    baseMonthly: "-",
    maintenance: "$150",
    support: "$100",
    setup: "$2000",
    icon: (
      <div className="flex gap-1 mb-2 text-blue-700">
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
      </div>
    ),
    popular: false,
    key: "large",
  },
];

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 40 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.6, type: "spring" as const },
  },
};

const buttonTap = { scale: 0.95 };
const buttonHover = { scale: 1.05 };
const STRIPE_PRICING_TABLE_ID = "prctbl_1SY4teBOpzfVpshIoMzl88QH";
const STRIPE_PUBLISHABLE_KEY =
  "pk_test_51MJZnDBOpzfVpshImYWVue7L4rpEeN7HdKB2d4D6JGZfcAK5U7f62MFy0QF5FPls488u2a491jSJehTDVD7F6QSZ006j10o1Pz";

const TestPricing: React.FC = () => {
  const [selectedTierKey, setSelectedTierKey] = React.useState<string | null>(
    null
  );
  const [includeMaintenance, setIncludeMaintenance] =
    React.useState<boolean>(false);
  const [includeSupport, setIncludeSupport] = React.useState<boolean>(false);
  const [freeTrialModalOpen, setFreeTrialModalOpen] = React.useState(false);
  const [trialPlanKey, setTrialPlanKey] = React.useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Open modal from query param if /pricing?freeTrialOpen=true
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("freeTrialOpen") === "true") {
      setFreeTrialModalOpen(true);
    }
  }, [location.search]);

  const selectedTier = React.useMemo(
    () => pricingTiers.find((t) => t.key === selectedTierKey) || null,
    [selectedTierKey]
  );
  const isStripeCheckoutTier = selectedTier?.key === "small";

  const parsePrice = (price: string): number => {
    const numeric = price.replace(/[^0-9.]/g, "");
    const parsed = parseFloat(numeric);
    return isNaN(parsed) ? 0 : parsed;
  };

  const totalMonthly = React.useMemo(() => {
    if (!selectedTier) return 0;
    let total = parsePrice(selectedTier.baseMonthly);
    if (includeMaintenance) total += parsePrice(selectedTier.maintenance);
    if (includeSupport) total += parsePrice(selectedTier.support);
    return total;
  }, [selectedTier, includeMaintenance, includeSupport]);

  const openModal = (tierKey: string) => {
    setSelectedTierKey(tierKey);
    setIncludeMaintenance(false);
    setIncludeSupport(false);
  };

  const closeModal = () => {
    setSelectedTierKey(null);
  };

  const buildPaymentUrl = (): string => {
    if (!selectedTier) return "/payment";
    const params = new URLSearchParams({ plan: selectedTier.key });
    if (includeMaintenance) params.set("maintenance", "true");
    if (includeSupport) params.set("support", "true");
    return `/payment?${params.toString()}`;
  };
  const fadeIn = {
    hidden: { opacity: 0, scale: 0.98, y: 40 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.7, type: "spring" as const },
    },
  };
  return (
    <section className="max-w-6xl mx-auto py-12 px-4">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="mb-12 text-center"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-blue-800 drop-shadow">
          RoomRounds Monthly TestPricing
        </h1>
        <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto">
          Simple monthly plans by facility size. Add support and maintenance at
          checkout.
        </p>
      </motion.div>

      {/* <div className="flex justify-center mb-6">
        <button
          onClick={() => setFreeTrialModalOpen(true)}
          className="bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold px-6 py-3 rounded-lg shadow hover:from-blue-700 hover:to-blue-600 transition text-lg"
        >
          Start Free Trial
        </button>
      </div> */}
      {/* MODAL for choosing plan for free trial */}
      <AnimatePresence>
        {freeTrialModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setFreeTrialModalOpen(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-lg max-w-lg w-full mx-4 p-8 relative"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-2xl text-gray-500 hover:text-gray-700"
                title="Close"
                onClick={() => setFreeTrialModalOpen(false)}
              >
                ×
              </button>
              <h3 className="text-2xl font-bold mb-4 text-blue-800 text-center">
                Choose Your Plan
              </h3>
              <div className="flex flex-col gap-4 mb-6">
                {pricingTiers.map((tier) => (
                  <label
                    key={tier.key}
                    className={`flex items-center border rounded-lg p-4 cursor-pointer gap-3 transition ${
                      trialPlanKey === tier.key
                        ? "border-blue-700 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="trialPlanGroup"
                      value={tier.key}
                      checked={trialPlanKey === tier.key}
                      onChange={() => setTrialPlanKey(tier.key)}
                      className="accent-blue-700 w-5 h-5"
                    />
                    <div className="flex flex-col flex-1">
                      <span className="font-bold text-blue-700">
                        {tier.name}
                      </span>
                      <span className="text-gray-600 text-sm">
                        {tier.bedRange}
                      </span>
                    </div>
                    <span className="text-blue-700 font-semibold">
                      {tier.baseMonthly}
                      {["large-medium", "large"].includes(tier.key)
                        ? " (Contact sales for pricing)"
                        : ""}
                    </span>
                  </label>
                ))}
              </div>
              <button
                disabled={!trialPlanKey}
                onClick={() => {
                  if (trialPlanKey) {
                    setFreeTrialModalOpen(false);
                    navigate(`/free-trial?plan=${trialPlanKey}`);
                  }
                }}
                className={`w-full bg-blue-600 text-white px-6 py-3 rounded-lg shadow font-semibold text-lg hover:bg-blue-700 transition ${
                  !trialPlanKey ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                Proceed
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {pricingTiers.map((tier) => (
          <motion.div
            key={tier.name}
            className={`relative bg-white rounded-2xl shadow-lg p-8 flex flex-col items-center border-2 transition-all duration-300 ${
              tier.popular
                ? "border-blue-600 scale-105 z-10 shadow-2xl"
                : "border-blue-600 scale-105 z-10 shadow-2xl"
              // : "border-transparent hover:border-blue-300 hover:scale-105"
            }`}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={cardVariants}
          >
            {/* {tier.popular && (
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow">
                Most Popular
              </span>
            )} */}
            {tier.icon}
            <h3 className="text-2xl font-extrabold mb-1 text-blue-800 tracking-tight">
              {tier.name}
            </h3>
            <div className="text-base font-medium text-blue-500 mb-4">
              {tier.bedRange}
            </div>
            <div className="w-full flex flex-col items-center mb-6">
              {/* Price section: hide actual price for Large-Medium and Large */}
              {["large-medium", "large"].includes(tier.key) ? (
                <>
                  <div className="text-sm text-gray-600 mb-1">Monthly</div>

                  {/* <div className="text-lg font-semibold text-blue-700">Contact sales for pricing</div> */}
                </>
              ) : (
                <>
                  <div className="text-sm text-gray-600 mb-1">Monthly</div>
                  <div className="text-3xl font-extrabold text-blue-700">
                    {tier.baseMonthly}
                  </div>
                </>
              )}
            </div>
            {["large-medium", "large"].includes(tier.key) ? (
              <Link
                to="/contact?pricing=true"
                className="w-full bg-blue-600 text-white px-6 mt-9 py-2 rounded-lg shadow hover:bg-blue-700 transition text-center"
              >
                Contact your RR sales representative for a custom pricing
                discussion
              </Link>
            ) : (
              <motion.button
                whileTap={buttonTap}
                whileHover={buttonHover}
                onClick={() => openModal(tier.key)}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-700 transition"
              >
                Select Plan
              </motion.button>
            )}
            {/* <p className="text-xs text-gray-500 mt-3 flex items-center justify-center gap-1">
              <FaLock className="inline text-gray-400" /> No long-term contracts. Cancel anytime.
            </p> */}
            {/* Hide extra Schedule Demo link for contact-only tiers to avoid duplication */}
            {/* {["large-medium", "large"].includes(tier.key) ? null : (
              <motion.div
                whileTap={buttonTap}
                whileHover={buttonHover}
                className="w-full mt-4"
              >
                <Link
                  to="/contact"
                  className="bg-gray-100 text-blue-700 px-6 py-2 rounded-lg shadow hover:bg-gray-200 transition w-full text-center block"
                >
                  Schedule Demo
                </Link>
              </motion.div>
            )} */}
          </motion.div>
        ))}
      </div>

      {selectedTier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white rounded-xl shadow-2xl max-w-lg w-[92%] p-6 z-10"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-blue-800">
                {selectedTier.name} — Monthly Options
              </h3>
              <button
                onClick={closeModal}
                aria-label="Close"
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="text-sm text-gray-600 mb-4">Base Monthly Fee</div>
            <div className="text-3xl font-extrabold text-blue-700 mb-4">
              {selectedTier.baseMonthly}
            </div>
            {isStripeCheckoutTier ? (
              <>
                <p className="text-sm text-gray-600">
                  Complete your Small plan purchase securely through Stripe
                  below. Selecting a plan in the widget will launch the Stripe
                  checkout flow.
                </p>
                <div className="mt-4 w-full rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <stripe-pricing-table
                    pricing-table-id={STRIPE_PRICING_TABLE_ID}
                    publishable-key={STRIPE_PUBLISHABLE_KEY}
                  ></stripe-pricing-table>
                </div>
                <button
                  onClick={closeModal}
                  className="mt-6 w-full px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <div className="border-t pt-4 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={includeMaintenance}
                      onChange={(e) => setIncludeMaintenance(e.target.checked)}
                    />
                    <div>
                      <div className="font-medium text-blue-800">
                        Add Maintenance Module
                      </div>
                      <div className="text-sm text-gray-600">
                        {selectedTier.maintenance} / month
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={includeSupport}
                      onChange={(e) => setIncludeSupport(e.target.checked)}
                    />
                    <div>
                      <div className="font-medium text-blue-800">
                        Add Support Package
                      </div>
                      <div className="text-sm text-gray-600">
                        {selectedTier.support} / month
                      </div>
                    </div>
                  </label>
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-gray-700">Total per month</div>
                  <div className="text-2xl font-bold text-blue-700">
                    ${totalMonthly.toFixed(0)}
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <Link
                    to={buildPaymentUrl()}
                    onClick={closeModal}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-700 transition"
                  >
                    Purchase
                  </Link>
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
            <div className="mt-3 text-xs text-gray-500">
              Setup fee applies upon activation: {selectedTier.setup} (one-time).
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default TestPricing;
